import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SERVICES } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  phone: "",
  email: "",
  service: "",
  address: "",
  message: "",
};

export default function QuoteForm({ compact = false, source = "hero" }) {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: typeof e === "string" ? e : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter at least your name and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        email: form.email.trim() || undefined,
        service: form.service || undefined,
        address: form.address.trim() || undefined,
        message: form.message.trim() || undefined,
        source,
      };
      await axios.post(`${API}/leads`, payload);
      setSuccess(true);
      toast.success("Got it! We'll call you back within minutes.");
      setForm(initial);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please call us directly.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div
        data-testid="quote-form-success"
        className="bg-white border border-slate-200 rounded-md p-8 text-center"
      >
        <CheckCircle2 className="text-emerald-500 mx-auto mb-3" size={40} />
        <h3 className="font-display text-2xl font-bold mb-2">Request received</h3>
        <p className="text-slate-600 mb-4">
          A team member will call you back shortly. For emergencies, call us now.
        </p>
        <button
          data-testid="quote-form-success-reset"
          onClick={() => setSuccess(false)}
          className="text-sm font-semibold text-orange-600 hover:underline"
        >
          Submit another request
        </button>
      </div>
    );
  }

  return (
    <form
      data-testid="quote-form"
      onSubmit={submit}
      className={`bg-white border border-slate-200 rounded-md ${compact ? "p-6" : "p-7 md:p-8"} shadow-[0_24px_60px_-30px_rgba(15,23,42,0.35)]`}
    >
      <div className="mb-5">
        <div className="label-eyebrow">Schedule an Appointment</div>
        <h3 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight mt-1">
          Book a Peak Plumbing visit
        </h3>
        <p className="text-sm text-slate-600 mt-1">
          Tell us what's going on — a team member will call to confirm your appointment.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-1">
          <Label htmlFor="qf-name" className="text-xs font-bold uppercase tracking-wider text-slate-600">Name *</Label>
          <Input
            id="qf-name"
            data-testid="quote-form-name"
            placeholder="Your full name"
            value={form.name}
            onChange={update("name")}
            required
            className="mt-1.5 h-11"
          />
        </div>
        <div className="sm:col-span-1">
          <Label htmlFor="qf-phone" className="text-xs font-bold uppercase tracking-wider text-slate-600">Phone *</Label>
          <Input
            id="qf-phone"
            data-testid="quote-form-phone"
            type="tel"
            placeholder="(586) 555-0123"
            value={form.phone}
            onChange={update("phone")}
            required
            className="mt-1.5 h-11"
          />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="qf-email" className="text-xs font-bold uppercase tracking-wider text-slate-600">Email (optional)</Label>
          <Input
            id="qf-email"
            data-testid="quote-form-email"
            type="email"
            placeholder="you@example.com"
            value={form.email}
            onChange={update("email")}
            className="mt-1.5 h-11"
          />
        </div>
        <div className="sm:col-span-1">
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Service Needed</Label>
          <Select value={form.service} onValueChange={(v) => setForm((s) => ({ ...s, service: v }))}>
            <SelectTrigger data-testid="quote-form-service" className="mt-1.5 h-11">
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent>
              {SERVICES.map((svc) => (
                <SelectItem key={svc.slug} value={svc.title} data-testid={`quote-form-service-${svc.slug}`}>
                  {svc.title}
                </SelectItem>
              ))}
              <SelectItem value="Other / Not sure">Other / Not sure</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-1">
          <Label htmlFor="qf-address" className="text-xs font-bold uppercase tracking-wider text-slate-600">Address (optional)</Label>
          <Input
            id="qf-address"
            data-testid="quote-form-address"
            placeholder="City or ZIP"
            value={form.address}
            onChange={update("address")}
            className="mt-1.5 h-11"
          />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="qf-message" className="text-xs font-bold uppercase tracking-wider text-slate-600">Briefly describe the issue</Label>
          <Textarea
            id="qf-message"
            data-testid="quote-form-message"
            placeholder="E.g., kitchen sink backed up, sewer smell in basement..."
            value={form.message}
            onChange={update("message")}
            rows={3}
            className="mt-1.5"
          />
        </div>
      </div>

      <button
        data-testid="quote-form-submit"
        type="submit"
        disabled={submitting}
        className="btn-cta mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 text-base disabled:opacity-70 disabled:cursor-not-allowed"
      >
        {submitting ? (
          <>
            <Loader2 size={18} className="animate-spin" /> Sending…
          </>
        ) : (
          <>
            <Send size={18} /> Schedule My Appointment
          </>
        )}
      </button>

      <p className="text-[11px] text-slate-500 mt-3 text-center">
        By submitting you agree to be contacted by Peak Plumbing. No spam, ever.
      </p>
    </form>
  );
}
